'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from '../contexts/SessionContext';

export default function EmailGate({ children }: {children: React.ReactNode;}) {
  const { isAuthenticated, initialized, setUserEmail } = useSession();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  // Don't render anything until session is initialized to prevent flicker
  if (!initialized) {
    return <div className="min-h-screen bg-background" />;
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Please enter your email address.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await setUserEmail(trimmed);
      router.push('/intent');
    } catch {
      // Even if Supabase fails, allow entry
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <span className="text-2xl font-bold tracking-tight text-foreground">doohthis</span>
          <p className="mt-2 text-sm text-muted-foreground">AI-Powered OOH Sales Intelligence</p>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-foreground mb-1">Welcome</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Enter your email to access the platform.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-medium text-foreground mb-1.5">

                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                placeholder="you@company.com"
                className={`w-full px-3.5 py-2.5 rounded-lg border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors ${
                error ? 'border-red-400' : 'border-border'}`
                } />

              {error &&
              <p className="mt-1.5 text-xs text-red-500">{error}</p>
              }
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">

              {loading ? 'Entering…' : "Let's doohthis"}
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">v2.5</p>

        <p className="mt-2 text-center text-xs text-muted-foreground">
          Your session activity will be tracked for analytics purposes.
        </p>
      </div>
    </div>);

}